// ── Wipe animation — pure math helpers (no React) ────────────────────────────
// Shared between wipeCard.jsx and IngestionLiveCard.jsx

export const EPS = 1e-4;
export const FULL_RECT   = "polygon(0 0, 100% 0, 100% 100%, 0 100%)";
export const COLLAPSE_BL = "polygon(0 100%, 0 100%, 0 100%, 0 100%)";
export const COLLAPSE_TR = "polygon(100% 0, 100% 0, 100% 0, 100% 0)";

export const DEFAULT_ANIMATION = { duration: 0.55, easing: [0.2, 0.8, 0.2, 1] };
export const DEFAULT_SLOPE     = { x: 1, y: 1 };

export function clamp01(v) { return Math.max(0, Math.min(1, v)); }
export function in01(v)    { return v >= -1e-9 && v <= 1 + 1e-9; }
export function toPct(p)   { return `${(p * 100).toFixed(4)}%`; }

export function dedupPts(pts) {
    const out = [];
    for (const p of pts) {
        if (!out.some((q) => Math.abs(q.x - p.x) < 1e-8 && Math.abs(q.y - p.y) < 1e-8))
            out.push(p);
    }
    return out;
}

export function intersections(t, slopeX, slopeY) {
    const tt = clamp01(t);
    const c  = (slopeX + slopeY) * tt;
    const pts = [];
    if (slopeY !== 0) {
        const yA = c / slopeY;
        if (in01(yA)) pts.push({ x: 0, y: yA });
        const yB = (c - slopeX) / slopeY;
        if (in01(yB)) pts.push({ x: 1, y: yB });
    }
    if (slopeX !== 0) {
        const xA = c / slopeX;
        if (in01(xA)) pts.push({ x: xA, y: 0 });
        const xB = (c - slopeY) / slopeX;
        if (in01(xB)) pts.push({ x: xB, y: 1 });
    }
    return dedupPts(pts).slice(0, 2);
}

export function polygonHalfPlane(t, below, slopeX, slopeY) {
    const tt = clamp01(t);
    const c  = (slopeX + slopeY) * tt;
    const corners = [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }];
    const inside  = (p) =>
        below
            ? slopeX * p.x + slopeY * p.y <= c + 1e-12
            : slopeX * p.x + slopeY * p.y >= c - 1e-12;
    let pts = corners.filter(inside).concat(intersections(tt, slopeX, slopeY));
    pts = dedupPts(pts);
    if (pts.length < 3)
        return below ? [{ x: 0, y: 0 }, { x: 0, y: 0 }, { x: 0, y: 0 }] : corners;
    const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
    const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
    pts.sort((a, b) => Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx));
    return pts;
}

export function clipPathFromPts(pts) {
    return `polygon(${pts.map((p) => `${toPct(p.x)} ${toPct(1 - p.y)}`).join(", ")})`;
}

export function setLine(divEl, t, slopeX, slopeY) {
    const pts = intersections(t, slopeX, slopeY);
    if (!pts[0] || !pts[1]) {
        divEl.style.clipPath = "polygon(0 0, 0 0, 0 0)";
        return;
    }
    const [p1, p2] = pts;
    const dx = p2.x - p1.x, dy = p2.y - p1.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 1e-8) return;
    const nx = -dy / len, ny = dx / len;
    const d  = 0.0013;
    const strip = [
        { x: p1.x + nx * d, y: p1.y + ny * d },
        { x: p2.x + nx * d, y: p2.y + ny * d },
        { x: p2.x - nx * d, y: p2.y - ny * d },
        { x: p1.x - nx * d, y: p1.y - ny * d },
    ];
    divEl.style.clipPath = `polygon(${strip
        .map((p) => `${(p.x * 100).toFixed(4)}% ${((1 - p.y) * 100).toFixed(4)}%`)
        .join(", ")})`;
}
